/**
 * RAM — TerminationOfAgent action.
 *
 * Terminates an agent / authorized-signer relationship. Writes an audit event;
 * the actual relationship removal is handled via `custRelAdd`-inverse on the
 * RCM side (legacy equivalent = CustRelDel, route PLANNED). We stage here.
 */
import { useState } from 'react';
import { Banner, Button, Pill } from '@/components/primitives';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useAuth } from '@/contexts/AuthContext';
import { asIso8601 } from '@retail-ops/shared-types';
import type { SessionEventPayload } from '@retail-ops/shared-types';
import type { RamTarget } from '../types';

export interface TerminationOfAgentProps {
  target: RamTarget;
  onDone: () => void;
  onCancel: () => void;
}

export default function TerminationOfAgent({
  target,
  onDone,
  onCancel,
}: TerminationOfAgentProps): JSX.Element {
  const client = useApiClient();
  const { user } = useAuth();

  const [agentCifId, setAgentCifId] = useState<string>('');
  const [effectiveDate, setEffectiveDate] = useState<string>('');
  const [reason, setReason] = useState<string>('');
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<boolean>(false);

  const canSave = !saving && agentCifId.trim().length > 0 && reason.trim().length > 0;

  const handleSave = async (): Promise<void> => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      const ev: SessionEventPayload = {
        eventType: 'relationship-removed',
        eventCategory: 'cif-account',
        severity: 'info',
        actor: user?.upn ?? 'fsr',
        timestamp: asIso8601(new Date().toISOString()),
        payloadJson: JSON.stringify({
          action: 'terminate-agent',
          primaryCifId: target.cifId,
          agentCifId,
          effectiveDate: effectiveDate || undefined,
          reason,
        }),
      };
      await client.retail.events.write(ev);
      setDone(true);
      setSaving(false);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Unable to record termination.');
      setSaving(false);
    }
  };

  if (done) {
    return (
      <div className="ram-wizard">
        <Banner tone="ok">
          Termination recorded. Route to RCM to remove the relationship rows.
        </Banner>
        <div className="ram-wizard__actions">
          <Button variant="primary" onClick={onDone}>
            Done
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="ram-wizard">
      <div className="ram-wizard__header">
        <h2 className="ram-wizard__title">Termination of agent</h2>
        <Pill variant="info">CIF {target.cifId}</Pill>
      </div>

      <Banner tone="info">
        This stages the audit trail only. The relationship row delete is handled
        in RCM (route TODO once `/accounts/cust-rel-del` ships).
      </Banner>

      <div className="ram-wizard__fields">
        <label className="ram-search__label">
          Agent CIF id
          <input
            className="ram-input"
            value={agentCifId}
            onChange={(e) => setAgentCifId(e.target.value)}
            maxLength={7}
          />
        </label>
        <label className="ram-search__label">
          Effective date
          <input
            className="ram-input"
            type="date"
            value={effectiveDate}
            onChange={(e) => setEffectiveDate(e.target.value)}
          />
        </label>
        <label className="ram-search__label" style={{ gridColumn: '1 / -1' }}>
          Reason
          <textarea
            className="ram-input"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={500}
          />
        </label>
      </div>

      {error && <Banner tone="err">{error}</Banner>}

      <div className="ram-wizard__actions">
        <Button variant="ghost" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={!canSave}>
          {saving ? 'Recording…' : 'Record termination'}
        </Button>
      </div>
    </div>
  );
}
